import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="comment-loader"
export default class extends Controller {
  static targets = ["loadingContainer", "commentsFrame"]
  static values = {
    commentableId: String,
    commentableType: String,
    url: String,
    loaded: { type: Boolean, default: false }
  }

  connect() {
    console.log("CommentLoader connected", this.element);
    
    // Listen for direct loadComments events
    this.element.addEventListener('loadComments', this.loadComments.bind(this));
    
    // If we're in a modal that's shown immediately, load comments
    if (this.element.closest('.comments-panel') && 
        document.body.classList.contains('comment-section-open')) {
      this.loadComments();
    }
  }

  loadComments(event) {
    if (event) event.preventDefault();
    
    console.log("Loading comments for", this.commentableTypeValue, this.commentableIdValue);
    
    // Don't reload if already loaded unless forced
    if (this.loadedValue && !event) return;
    
    // Show loading state
    this.showLoading();
    
    // Build the URL based on commentable type and id
    const url = this.hasUrlValue ? this.urlValue : this._buildCommentsUrl();
    
    console.log("Fetching comments from", url);
    
    // Use Turbo to fetch the comments
    fetch(url, {
      headers: {
        "Accept": "text/vnd.turbo-stream.html",
        "X-Requested-With": "XMLHttpRequest"
      }
    })
    .then(response => {
      if (!response.ok) throw new Error('Network response was not ok');
      return response.text();
    })
    .then(html => {
      // Process the Turbo Stream response
      console.log("Received response", html.substring(0, 100) + "...");
      this.processResponse(html);
      this.loadedValue = true;
    })
    .catch(error => {
      console.error('Error loading comments:', error);
      this.showError();
    })
    .finally(() => {
      this.hideLoading();
    });
  }

  sortComments(event) {
    const sortValue = event.target.value;
    
    console.log("Sorting comments by", sortValue);
    
    // Show loading state
    this.showLoading();
    
    // Build the URL based on commentable type and id with sort parameter
    const url = `${this._buildCommentsUrl()}?sort=${sortValue}`;
    
    // Use Turbo to fetch the sorted comments
    fetch(url, {
      headers: {
        "Accept": "text/vnd.turbo-stream.html",
        "X-Requested-With": "XMLHttpRequest"
      }
    })
    .then(response => {
      if (!response.ok) throw new Error('Network response was not ok');
      return response.text();
    })
    .then(html => {
      // Process the Turbo Stream response
      this.processResponse(html);
    })
    .catch(error => {
      console.error('Error sorting comments:', error);
      this.showError();
    })
    .finally(() => {
      this.hideLoading();
    });
  }

  processResponse(html) {
    // Let Turbo handle the stream response
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const turboStream = doc.querySelector('turbo-stream');
    
    if (turboStream) {
      console.log("Found turbo stream, appending to body");
      document.body.appendChild(turboStream);
    } else {
      console.error("No turbo-stream found in response");
      console.log("Response HTML:", html);
    }
  }

  showLoading() {
    const frame = this._commentsFrameElement();
    if (frame) {
      this._previousFrameDisplay = frame.style.display;
      frame.style.display = 'none';
    }

    if (this.hasLoadingContainerTarget) {
      this.loadingContainerTarget.classList.add('active');
    }
  }

  hideLoading() {
    if (this.hasLoadingContainerTarget) {
      this.loadingContainerTarget.classList.remove('active');
    }

    const frame = this._commentsFrameElement();
    if (frame) {
      frame.style.display = this._previousFrameDisplay ?? '';
      this._previousFrameDisplay = undefined;
    }
  }

  showError() {
    const frame = this._commentsFrameElement();
    if (frame) {
      frame.style.display = '';
      frame.innerHTML = `
        <div class="comments-error">
          <p>Error loading comments. Please try again.</p>
          <button class="btn btn-primary" data-action="click->comment-loader#loadComments">
            Retry
          </button>
        </div>
      `;
    }
  }

  _buildCommentsUrl() {
    const type = this.commentableTypeValue.toLowerCase();
    const id = this.commentableIdValue;
    
    // Handle different commentable types
    if (type === 'post') {
      return `/posts/${id}/comments`;
    } else if (type === 'academypost') {
      return `/academy_posts/${id}/comments`;
    }
    
    // Default fallback
    return `/comments?commentable_type=${type}&commentable_id=${id}`;
  }

  _commentsFrameElement() {
    if (this.hasCommentsFrameTarget) {
      return this.commentsFrameTarget;
    }

    if (this.hasCommentableIdValue) {
      return document.getElementById(`comments-frame-${this.commentableIdValue}`);
    }

    return null;
  }
}
